// In-memory app state. The Word document lives here only; it is never stored or uploaded.
import { BUILT_IN_PROFILES, forkProfile } from "./profiles/index.js";
import { createStore, getLocalStorage } from "./storage.js";

export const store = createStore(typeof window === "undefined" ? null : getLocalStorage());

export const state = {
  doc: null, // { name, data: ArrayBuffer }
  result: null, // { html, messages, profileJson } for doc + current conversion profile
  profileId: store.getSelected() ?? "html5",
  customs: store.listCustom(),
  working: null, // profile being edited
  saved: null, // last saved/loaded version of it (null for brand-new, unsaved profiles)
  ruleDraft: null, // { index: "new", rule } prefilled from an unmapped style
  previewSource: "sample", // "document" | "sample"
  editingFromResult: false,
  filters: { search: "", show: "all", type: "any" },
  demoProfile: null, // loaded by the "Try a demo" page for this session only; read-only like a built-in
};

export const allProfiles = () => [...BUILT_IN_PROFILES, ...(state.demoProfile ? [state.demoProfile] : []), ...state.customs];

export function setDemoProfile(profile) {
  state.demoProfile = { ...profile, builtIn: true };
}
export const findProfile = (id) => allProfiles().find((profile) => profile.id === id) ?? null;
export const takenIds = () => new Set(allProfiles().map((profile) => profile.id));
export const takenNames = () => new Set(state.customs.map((profile) => profile.name.toLowerCase()));
export const conversionProfile = () => findProfile(state.profileId) ?? BUILT_IN_PROFILES[0];

export function setProfileId(id) {
  state.profileId = id;
  state.result = null;
  store.setSelected(id);
}

export function setCustoms(list) {
  state.customs = list;
}

export function openForEditing(profile) {
  state.working = structuredClone(profile);
  state.saved = structuredClone(profile);
  state.ruleDraft = null;
}

// For new or imported profiles that haven't been saved yet.
export function startUnsaved(profile) {
  state.working = structuredClone(profile);
  state.saved = null;
  state.ruleDraft = null;
}

export function ensureWorking(id) {
  if (state.working?.id === id) return state.working;
  const profile = findProfile(id);
  if (!profile) return null;
  openForEditing(profile);
  return state.working;
}

export const isDirty = () => state.working !== null && JSON.stringify(state.working) !== JSON.stringify(state.saved);
export const isSavedCustom = () => Boolean(state.working) && state.customs.some((p) => p.id === state.working.id);

// Applies an edit. A built-in is forked into a custom copy first; returns true when that happened.
export function updateWorking(next) {
  if (state.working.builtIn) {
    const fork = forkProfile(state.working, takenIds(), takenNames());
    state.working = {
      ...next,
      id: fork.id,
      name: next.name === state.working.name ? fork.name : next.name,
      builtIn: false,
      basedOn: fork.basedOn,
    };
    return true;
  }
  state.working = next;
  return false;
}

export function saveWorking() {
  state.customs = store.saveCustom(state.working);
  state.saved = structuredClone(state.working);
  if (state.editingFromResult) setProfileId(state.working.id);
  else if (state.profileId === state.working.id) state.result = null;
}

export function discardWorking() {
  state.working = state.saved ? structuredClone(state.saved) : null;
  state.ruleDraft = null;
}

export function deleteCustomProfile(id) {
  state.customs = store.deleteCustom(id);
  if (state.profileId === id) setProfileId("html5");
}
